import SaveSubjectTypeDefinition from '@Application/SubjectTypeDefinitions/Commands/SaveSubjectTypeDefinition';
import type { FieldError } from '@Infra/FetchLib/ApiError';
import SubjectTypeGrantsFormData from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import type {
	GrantDraft,
	GrantKind,
} from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import isAboutLabelValid from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/constraint/isAboutLabelValid';
import isSheetTitleValid from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/constraint/isSheetTitleValid';
import type SubjectFieldDraftView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectFieldDraftView';
import type SubjectTypeDefinitionView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export type WordingPart =
	| 'singular'
	| 'plural'
	| 'createAction'
	| 'emptyList'
	| 'sheetTitle'
	| 'aboutLabel'
	| 'titleLabel';

export type SpeakerRoleDraft = {
	/** Null until the server derives it on the first save. */
	readonly key: string | null;
	readonly label: string;
};

type ServerMessages = Readonly<Record<string, string>>;

type SubjectTypeDefinitionFormArgs = {
	singular: string;
	plural: string;
	createAction: string;
	emptyList: string;
	/** Blank means no title; the server then requires the sheet to be empty. */
	sheetTitle: string;
	/** Only required while `about` is not empty: the field is hidden otherwise. */
	aboutLabel: string;
	titleLabel: string;
	/** The `value` of the chosen icon. */
	icon: string;
	/** The `value` of the chosen color. */
	color: string;
	informationMode: string;
	membersCanShare: boolean;
	about: readonly string[];
	fields: readonly SubjectFieldDraftView[];
	speakerRoles: readonly SpeakerRoleDraft[];
	grants: SubjectTypeGrantsFormData;
	workflowKeys: readonly string[];
	serverMessages: ServerMessages;
};

const DEFAULT_INFORMATION_MODE = 'notice';
const DEFAULT_ICON = 'folder';
const DEFAULT_COLOR = 'cyan';

/**
 * Immutable form state of a dossier type definition: wording, sheet fields (archived ones included, in
 * the saved order), populations, access grants, sharing, speaker roles and proposed workflows.
 *
 * Server errors do not go through `updateValidationFromExternalSource`: that route calls
 * `updateFieldsValidationsResult`, which adds the field to `ignoreFields`, an object shared by every
 * instance of the class, so one server error would switch off the local validation of that field for
 * good. Errors are instead held by path in `serverMessages` (`wording.*`, `fields.<i>.*`,
 * `usableBy.<i>.id`, `usableBy`, `workflowKeys`…), read back by the `get…ServerMessage` methods, and
 * cleared when the part they concern is edited.
 */
@ValidatedClass()
class SubjectTypeDefinitionFormData extends AbstractValidatedClass<SubjectTypeDefinitionFormArgs> {
	@constraint(Joi.string().max(60), () => '60 caractères au plus.')
	@constraint(Joi.string().pattern(/\S/).required(), () => 'Le nom au singulier est requis.')
	public readonly singular: string;

	@constraint(Joi.string().max(60), () => '60 caractères au plus.')
	@constraint(Joi.string().pattern(/\S/).required(), () => 'Le nom au pluriel est requis.')
	public readonly plural: string;

	@constraint(Joi.string().max(120), () => '120 caractères au plus.')
	@constraint(Joi.string().pattern(/\S/).required(), () => 'Le libellé du bouton de création est requis.')
	public readonly createAction: string;

	@constraint(Joi.string().max(255), () => '255 caractères au plus.')
	@constraint(Joi.string().pattern(/\S/).required(), () => 'Le message de liste vide est requis.')
	public readonly emptyList: string;

	@constraint(Joi.string().allow('').max(120), () => '120 caractères au plus.')
	@constraint(isSheetTitleValid, () => 'Le titre de la fiche est requis dès que la fiche compte un champ.')
	public readonly sheetTitle: string;

	@constraint(Joi.string().allow('').max(60), () => '60 caractères au plus.')
	@constraint(isAboutLabelValid, () => 'Ce champ est obligatoire.')
	public readonly aboutLabel: string;

	@constraint(Joi.string().max(60), () => '60 caractères au plus.')
	@constraint(Joi.string().pattern(/\S/).required(), () => 'Ce champ est obligatoire.')
	public readonly titleLabel: string;

	public readonly icon: string;
	public readonly color: string;
	public readonly informationMode: string;
	public readonly membersCanShare: boolean;
	public readonly about: readonly string[];
	public readonly fields: readonly SubjectFieldDraftView[];
	public readonly speakerRoles: readonly SpeakerRoleDraft[];
	public readonly grants: SubjectTypeGrantsFormData;
	public readonly workflowKeys: readonly string[];
	public readonly serverMessages: ServerMessages;

	constructor(data: SubjectTypeDefinitionFormArgs) {
		super(data);
		this.singular = data.singular;
		this.plural = data.plural;
		this.createAction = data.createAction;
		this.emptyList = data.emptyList;
		this.sheetTitle = data.sheetTitle;
		this.aboutLabel = data.aboutLabel;
		this.titleLabel = data.titleLabel;
		this.icon = data.icon;
		this.color = data.color;
		this.informationMode = data.informationMode;
		this.membersCanShare = data.membersCanShare;
		this.about = Object.freeze([...data.about]);
		this.fields = Object.freeze([...data.fields]);
		this.speakerRoles = Object.freeze([...data.speakerRoles]);
		this.grants = data.grants;
		this.workflowKeys = Object.freeze([...data.workflowKeys]);
		this.serverMessages = Object.freeze({ ...data.serverMessages });
		Object.freeze(this);
	}

	/** A blank type: nothing to name yet, no field, nobody granted. */
	static createEmpty(): SubjectTypeDefinitionFormData {
		return new SubjectTypeDefinitionFormData({
			singular: '',
			plural: '',
			createAction: '',
			emptyList: '',
			sheetTitle: '',
			aboutLabel: 'Porte sur',
			titleLabel: 'Intitulé',
			icon: DEFAULT_ICON,
			color: DEFAULT_COLOR,
			informationMode: DEFAULT_INFORMATION_MODE,
			membersCanShare: false,
			about: [],
			fields: [],
			speakerRoles: [],
			grants: SubjectTypeGrantsFormData.createEmpty(),
			workflowKeys: [],
			serverMessages: {},
		});
	}

	static fromView(view: SubjectTypeDefinitionView): SubjectTypeDefinitionFormData {
		return new SubjectTypeDefinitionFormData({
			singular: view.wording.singular,
			plural: view.wording.plural,
			createAction: view.wording.createAction,
			emptyList: view.wording.emptyList,
			sheetTitle: view.wording.sheetTitle ?? '',
			aboutLabel: view.wording.aboutLabel,
			titleLabel: view.wording.titleLabel,
			icon: view.icon.value,
			color: view.color.value,
			informationMode: view.informationMode.value,
			membersCanShare: view.membersCanShare,
			about: view.about.map((entry) => entry.value),
			fields: view.fields,
			speakerRoles: view.speakerRoles.map((role) => ({ key: role.key, label: role.label })),
			grants: SubjectTypeGrantsFormData.fromGrants(view.usableBy),
			workflowKeys: view.workflowKeys,
			serverMessages: {},
		});
	}

	/** The `POST` / `PUT` command; `id` is `null` to create. */
	toCommand(accountId: number, id: number | null): SaveSubjectTypeDefinition {
		return new SaveSubjectTypeDefinition(accountId, id, {
			wording: {
				singular: this.singular.trim(),
				plural: this.plural.trim(),
				createAction: this.createAction.trim(),
				emptyList: this.emptyList.trim(),
				sheetTitle: this.sheetTitle,
				aboutLabel: this.aboutLabel,
				titleLabel: this.titleLabel,
			},
			icon: this.icon,
			color: this.color,
			informationMode: this.informationMode,
			membersCanShare: this.membersCanShare,
			about: this.about,
			fields: this.fields,
			speakerRoles: this.speakerRoles,
			usableBy: this.grants.usableBy,
			workflowKeys: this.workflowKeys,
		});
	}

	setWording(part: WordingPart, value: string): this {
		return this.update({ [part]: value }, `wording.${part}`);
	}

	setIcon(icon: string): this {
		return this.update({ icon }, 'icon');
	}

	setColor(color: string): this {
		return this.update({ color }, 'color');
	}

	setInformationMode(informationMode: string): this {
		return this.update({ informationMode }, 'informationMode');
	}

	setMembersCanShare(membersCanShare: boolean): this {
		return this.update({ membersCanShare });
	}

	setAbout(about: readonly string[]): this {
		return this.update({ about }, 'about');
	}

	addField(draft: SubjectFieldDraftView): this {
		return this.withFields([...this.fields, draft]);
	}

	replaceField(index: number, draft: SubjectFieldDraftView): this {
		if (this.fields[index] === undefined) {
			return this;
		}

		return this.withFields(this.fields.map((field, position) => (position === index ? draft : field)));
	}

	/**
	 * Swaps the field with its nearest neighbour in the same list (active or archived), so an archived
	 * field lying between two active ones is jumped over. Out-of-range moves return the instance unchanged.
	 */
	moveField(index: number, offset: -1 | 1): this {
		const moved = this.fields[index];

		if (moved === undefined) {
			return this;
		}

		const targetIndex = this.findNeighbourIndex(moved, index, offset);
		const target = this.fields[targetIndex];

		if (target === undefined) {
			return this;
		}

		const fields = [...this.fields];
		fields[index] = target;
		fields[targetIndex] = moved;

		return this.withFields(fields);
	}

	/** Archives a saved field; a field not yet saved is removed with `removeNewField` instead. */
	archiveField(index: number): this {
		const field = this.fields[index];

		if (field === undefined || field.key === null) {
			return this;
		}

		return this.replaceField(index, field.with({ archived: true }));
	}

	restoreField(index: number): this {
		const field = this.fields[index];

		if (field === undefined) {
			return this;
		}

		return this.replaceField(index, field.with({ archived: false }));
	}

	/** Drops a field that was never saved; a saved field is never removed, only archived. */
	removeNewField(index: number): this {
		if (this.fields[index]?.key !== null) {
			return this;
		}

		return this.withFields(this.fields.filter((_field, position) => position !== index));
	}

	/** A blank label is allowed: the row is then filled in, and the server refuses it if left empty. */
	addSpeakerRole(label: string): this {
		return this.update({ speakerRoles: [...this.speakerRoles, { key: null, label }] }, 'speakerRoles');
	}

	renameSpeakerRole(index: number, label: string): this {
		return this.update(
			{
				speakerRoles: this.speakerRoles.map((role, position) =>
					position === index ? { key: role.key, label } : role,
				),
			},
			'speakerRoles',
		);
	}

	removeSpeakerRole(index: number): this {
		return this.update(
			{ speakerRoles: this.speakerRoles.filter((_role, position) => position !== index) },
			'speakerRoles',
		);
	}

	get usableBy(): readonly GrantDraft[] {
		return this.grants.usableBy;
	}

	/** Replaces the access lines with what `update` makes of them. */
	updateGrants(update: (grants: SubjectTypeGrantsFormData) => SubjectTypeGrantsFormData): this {
		const grants = update(this.grants);

		if (grants === this.grants) {
			return this;
		}

		return this.cloneWith({ grants } as Partial<SubjectTypeDefinitionFormArgs>);
	}

	addGrant(kind: GrantKind, id: number, level: string): this {
		return this.updateGrants((grants) => grants.addGrant(kind, id, level));
	}

	setGrantLevel(index: number, level: string): this {
		return this.updateGrants((grants) => grants.setGrantLevel(index, level));
	}

	removeGrant(index: number): this {
		return this.updateGrants((grants) => grants.removeGrant(index));
	}

	hasGrant(kind: GrantKind, id: number): boolean {
		return this.grants.hasGrant(kind, id);
	}

	setWorkflowKeys(workflowKeys: readonly string[]): this {
		return this.update({ workflowKeys }, 'workflowKeys');
	}

	/** Replaces the server messages with `fieldErrors`, the first message of a path winning. */
	applyServerErrors(fieldErrors: readonly FieldError[]): this {
		const serverMessages: Record<string, string> = {};

		for (const { field, message } of fieldErrors) {
			serverMessages[field] ??= message;
		}

		return this.cloneWith({
			serverMessages,
			grants: this.grants.applyServerErrors(fieldErrors),
		} as Partial<SubjectTypeDefinitionFormArgs>);
	}

	/** Message to show under a wording input: the server's if any, else the local validation's. */
	getWordingErrorMessage(part: WordingPart): string | undefined {
		return this.getWordingServerMessage(part) ?? this.getValidationMessage(part);
	}

	isWordingShownValid(part: WordingPart): boolean {
		return this.getWordingServerMessage(part) === null && this.isValid(part);
	}

	/** Server path `wording.<part>`. */
	getWordingServerMessage(part: WordingPart): string | null {
		return this.messageAt(`wording.${part}`);
	}

	/** Server paths `fields.<index>.*`: the first message about the field at `index`. */
	getFieldServerMessage(index: number): string | null {
		return this.messageUnder(`fields.${index}`);
	}

	/** Server paths `usableBy.<index>.*`: the access line at `index`. */
	getGrantServerMessage(index: number): string | null {
		return this.grants.getGrantServerMessage(index);
	}

	/** Server path `usableBy`: a message about the grants as a whole. */
	getUsableByServerMessage(): string | null {
		return this.grants.getUsableByServerMessage();
	}

	/** Server path `workflowKeys`. */
	getWorkflowKeysServerMessage(): string | null {
		return this.messageAt('workflowKeys');
	}

	/** Server paths `speakerRoles.<index>.*`. */
	getSpeakerRoleServerMessage(index: number): string | null {
		return this.messageUnder(`speakerRoles.${index}`);
	}

	/** Server path `about`. */
	getAboutServerMessage(): string | null {
		return this.messageAt('about');
	}

	/** Server path `icon`. */
	getIconServerMessage(): string | null {
		return this.messageAt('icon');
	}

	/** Server path `color`. */
	getColorServerMessage(): string | null {
		return this.messageAt('color');
	}

	/** Server path `informationMode`. */
	getInformationModeServerMessage(): string | null {
		return this.messageAt('informationMode');
	}

	/** Server path `speakerRoles`: a message about the roles as a whole. */
	getSpeakerRolesServerMessage(): string | null {
		return this.messageAt('speakerRoles');
	}

	private findNeighbourIndex(moved: SubjectFieldDraftView, index: number, offset: -1 | 1): number {
		let targetIndex = index + offset;

		while (targetIndex >= 0 && targetIndex < this.fields.length) {
			if (this.fields[targetIndex]?.archived === moved.archived) {
				return targetIndex;
			}

			targetIndex += offset;
		}

		return -1;
	}

	private messageAt(path: string): string | null {
		return this.serverMessages[path] ?? null;
	}

	private messageUnder(prefix: string): string | null {
		const path = Object.keys(this.serverMessages).find(
			(candidate) => candidate === prefix || candidate.startsWith(`${prefix}.`),
		);

		return path === undefined ? null : this.messageAt(path);
	}

	/** Indices shift when fields are added, removed or moved, so every field message is dropped. */
	private withFields(fields: readonly SubjectFieldDraftView[]): this {
		return this.update({ fields }, 'fields', 'wording.sheetTitle');
	}

	/** Applies `changes` and clears the server messages found at or under each of `clearedPaths`. */
	private update(changes: Partial<SubjectTypeDefinitionFormArgs>, ...clearedPaths: string[]): this {
		const serverMessages = Object.fromEntries(
			Object.entries(this.serverMessages).filter(
				([path]) =>
					!clearedPaths.some((cleared) => path === cleared || path.startsWith(`${cleared}.`)),
			),
		);

		return this.cloneWith({ ...changes, serverMessages } as Partial<SubjectTypeDefinitionFormArgs>);
	}
}

export default SubjectTypeDefinitionFormData;
